// Screen 4 — Body information (wizard step 3 of 9)
//
// Adds `calculation_basis` for customers whose identity is "Other" or
// "Prefer not to say" — the equation basis (or opt-out) is captured
// separately from identity and used only for calorie calculation.

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardScreen } from '../../components/planner/WizardScreen';
import { FieldError } from '../../components/planner/FieldError';
import {
  useDraft,
  STEP_ROUTES,
  isBodyValid,
} from '../../lib/plannerDraft';
import { ftInToCm, lbToKg } from '../../lib/plannerCalc';
import { ACTIVITY_LABELS } from '../../lib/plannerRules';

const SEXES = [
  { id: 'male', label: 'Male' },
  { id: 'female', label: 'Female' },
  { id: 'other', label: 'Other' },
  { id: 'prefer_not_to_say', label: 'Prefer not' },
];

const CALC_BASIS = [
  { id: 'male_equation',   label: 'Use the male equation' },
  { id: 'female_equation', label: 'Use the female equation' },
  { id: 'manual',          label: 'I will enter my own calorie target' },
];

export default function Step4Body() {
  const nav = useNavigate();
  const { draft, patchBody, patch, bump } = useDraft();
  const b = draft.body || {};
  const [errs, setErrs] = useState({});

  const needsBasis = b.sex === 'other' || b.sex === 'prefer_not_to_say';

  // Normalized values (live) --------------------------------------
  const norm = useMemo(() => {
    const height_cm =
      b.height_unit === 'imperial'
        ? (b.height_ft || b.height_in ? ftInToCm(b.height_ft, b.height_in) : null)
        : Number(b.height_cm) || null;
    const weight_kg =
      b.weight_unit === 'lb'
        ? (b.weight_value ? lbToKg(b.weight_value) : null)
        : Number(b.weight_value) || null;
    return {
      height_cm: height_cm ? Math.round(height_cm * 10) / 10 : null,
      weight_kg: weight_kg ? Math.round(weight_kg * 10) / 10 : null,
    };
  }, [b.height_unit, b.height_ft, b.height_in, b.height_cm, b.weight_unit, b.weight_value]);

  const setSex = (s) => {
    // Changing sex clears calculation_basis when the new sex doesn't need one.
    const upd = { sex: s };
    if (s !== 'other' && s !== 'prefer_not_to_say') upd.calculation_basis = null;
    patchBody(upd);
  };

  const onContinue = () => {
    const nextErrs = {};
    const age = Number(b.age);
    if (!(Number.isFinite(age) && age >= 13 && age <= 100))
      nextErrs.age = 'Please enter your age (13–100).';
    if (!b.sex) nextErrs.sex = 'Please choose an option.';
    if (needsBasis && !b.calculation_basis)
      nextErrs.calc_basis = 'Please choose which calculation to use.';
    if (!(Number.isFinite(norm.height_cm) && norm.height_cm >= 120 && norm.height_cm <= 230))
      nextErrs.height = 'Please enter a valid height (120–230 cm / ~3′11″–7′7″).';
    if (!(Number.isFinite(norm.weight_kg) && norm.weight_kg >= 30 && norm.weight_kg <= 300))
      nextErrs.weight = 'Please enter a valid weight (30–300 kg / ~66–661 lb).';
    if (!b.activity_level) nextErrs.activity = 'Please select an activity level.';
    setErrs(nextErrs);
    if (Object.keys(nextErrs).length) return;

    // Persist normalized values then recompute downstream + clear plan cache.
    patchBody(
      {
        normalized_height_cm: norm.height_cm,
        normalized_weight_kg: norm.weight_kg,
      },
      { recompute: true, invalidateGeneration: true },
    );

    // If the customer opted to enter calories manually, pre-select
    // "custom" on the next screen so the estimate card is hidden and
    // they aren't forced through an unnecessary toggle.
    if (b.calculation_basis === 'manual') {
      patch(
        { calorie_source: 'custom', calorie_target: null },
        { invalidateGeneration: true },
      );
    } else if (draft.calorie_source == null) {
      // First-time visit; default to calculated for calc-eligible cases.
      patch({ calorie_source: 'calculated' }, { invalidateGeneration: true });
    }

    // Sanity re-check against centralized validator.
    if (!isBodyValid({ ...draft, body: { ...b, normalized_height_cm: norm.height_cm, normalized_weight_kg: norm.weight_kg } })) {
      // Should not happen if per-field checks passed; guard anyway.
      return;
    }

    bump(4);
    nav(STEP_ROUTES[4]);
  };

  return (
    <WizardScreen
      step={3}
      eyebrow="About you"
      title="A little about you"
      onContinue={onContinue}
    >
      <p className="text-sm mb-6" style={{ color: 'var(--text-dim)' }}>
        We use this only to estimate your calorie needs.
      </p>

      {/* Age --------------------------------------------------- */}
      <label className="block mb-6">
        <span className="font-display uppercase tracking-wider text-sm">Age</span>
        <input
          type="number"
          inputMode="numeric"
          min={13}
          max={100}
          value={b.age}
          onChange={(e) => patchBody({ age: e.target.value }, { invalidateGeneration: true })}
          data-testid="body-age-input"
          className="mt-2 w-full md:w-48"
          style={inputStyle}
          aria-invalid={Boolean(errs.age)}
          aria-describedby={errs.age ? 'age-error' : undefined}
        />
        <FieldError id="age-error">{errs.age}</FieldError>
      </label>

      {/* Sex --------------------------------------------------- */}
      <fieldset className="mb-6">
        <legend className="font-display uppercase tracking-wider text-sm mb-2">Sex</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {SEXES.map((s) => {
            const selected = b.sex === s.id;
            return (
              <button
                key={s.id}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setSex(s.id)}
                data-testid={`body-sex-${s.id}-btn`}
                className="btn"
                style={{
                  padding: '10px 16px',
                  fontSize: '13px',
                  background: selected ? 'var(--orange)' : '#fff',
                  color: selected ? '#fff' : 'var(--ink)',
                  border: selected ? '2px solid var(--orange)' : '1.5px solid var(--line)',
                  letterSpacing: '0.18em',
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <FieldError id="sex-error">{errs.sex}</FieldError>
      </fieldset>

      {/* Calculation basis (only when needed) ---------------- */}
      {needsBasis && (
        <fieldset className="mb-6" data-testid="body-calc-basis-fieldset">
          <legend className="font-display uppercase tracking-wider text-sm mb-2">
            Which calculation should we use?
          </legend>
          <div role="radiogroup" className="grid gap-2">
            {CALC_BASIS.map((o) => {
              const selected = b.calculation_basis === o.id;
              return (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => patchBody({ calculation_basis: o.id }, { invalidateGeneration: true })}
                  data-testid={`body-calc-basis-${o.id}-btn`}
                  className="p-3 text-left"
                  style={{
                    background: selected ? 'rgba(239,104,32,0.08)' : '#fff',
                    border: selected ? '2px solid var(--orange)' : '1.5px solid var(--line)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  <div className="font-medium">{o.label}</div>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }}>
            Used only for the calorie calculation — this does not change your identity above.
          </p>
          <FieldError id="calc-basis-error">{errs.calc_basis}</FieldError>
        </fieldset>
      )}

      {/* Height ------------------------------------------------ */}
      <fieldset className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <legend className="font-display uppercase tracking-wider text-sm">Height</legend>
          <div className="flex" style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--line)' }}>
            {['imperial', 'metric'].map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => patchBody({ height_unit: u }, { invalidateGeneration: true })}
                className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5"
                style={{
                  background: b.height_unit === u ? 'var(--ink)' : '#fff',
                  color: b.height_unit === u ? 'var(--cream)' : 'var(--ink)',
                }}
                data-testid={`body-height-unit-${u}-btn`}
              >
                {u === 'imperial' ? 'ft / in' : 'cm'}
              </button>
            ))}
          </div>
        </div>
        {b.height_unit === 'imperial' ? (
          <div className="flex gap-3">
            <div className="flex items-center gap-2">
              <input
                type="number" inputMode="numeric" aria-label="Height feet"
                min={3} max={7}
                value={b.height_ft}
                onChange={(e) => patchBody({ height_ft: e.target.value }, { invalidateGeneration: true })}
                data-testid="body-height-ft-input"
                className="w-24" style={inputStyle}
              />
              <span className="text-sm">ft</span>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number" inputMode="numeric" aria-label="Height inches"
                min={0} max={11}
                value={b.height_in}
                onChange={(e) => patchBody({ height_in: e.target.value }, { invalidateGeneration: true })}
                data-testid="body-height-in-input"
                className="w-24" style={inputStyle}
              />
              <span className="text-sm">in</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="number" inputMode="numeric" aria-label="Height in centimeters"
              min={120} max={230}
              value={b.height_cm}
              onChange={(e) => patchBody({ height_cm: e.target.value }, { invalidateGeneration: true })}
              data-testid="body-height-cm-input"
              className="w-40" style={inputStyle}
            />
            <span className="text-sm">cm</span>
          </div>
        )}
        {norm.height_cm && (
          <div className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }} data-testid="body-height-normalized">
            Normalized: {norm.height_cm} cm
          </div>
        )}
        <FieldError id="height-error">{errs.height}</FieldError>
      </fieldset>

      {/* Weight ----------------------------------------------- */}
      <fieldset className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <legend className="font-display uppercase tracking-wider text-sm">Weight</legend>
          <div className="flex" style={{ borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--line)' }}>
            {['lb', 'kg'].map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => patchBody({ weight_unit: u, weight_value: '' }, { invalidateGeneration: true })}
                className="text-xs font-semibold uppercase tracking-wider px-3 py-1.5"
                style={{
                  background: b.weight_unit === u ? 'var(--ink)' : '#fff',
                  color: b.weight_unit === u ? 'var(--cream)' : 'var(--ink)',
                }}
                data-testid={`body-weight-unit-${u}-btn`}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number" inputMode="numeric"
            aria-label={`Weight in ${b.weight_unit}`}
            value={b.weight_value}
            onChange={(e) => patchBody({ weight_value: e.target.value }, { invalidateGeneration: true })}
            data-testid="body-weight-input"
            className="w-40" style={inputStyle}
          />
          <span className="text-sm">{b.weight_unit}</span>
        </div>
        {norm.weight_kg && (
          <div className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }} data-testid="body-weight-normalized">
            Normalized: {norm.weight_kg} kg
          </div>
        )}
        <FieldError id="weight-error">{errs.weight}</FieldError>
      </fieldset>

      {/* Activity ---------------------------------------------- */}
      <fieldset className="mb-2">
        <legend className="font-display uppercase tracking-wider text-sm mb-2">Activity level</legend>
        <select
          value={b.activity_level || ''}
          onChange={(e) =>
            patchBody({ activity_level: e.target.value || null }, { invalidateGeneration: true })
          }
          data-testid="body-activity-select"
          style={{ ...inputStyle, appearance: 'auto' }}
          className="w-full md:w-96"
        >
          <option value="">Choose one…</option>
          {Object.entries(ACTIVITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <FieldError id="activity-error">{errs.activity}</FieldError>
      </fieldset>
    </WizardScreen>
  );
}

const inputStyle = {
  padding: '12px 14px',
  border: '1.5px solid var(--line)',
  borderRadius: 'var(--radius)',
  background: '#fff',
  fontSize: '16px',
  fontFamily: 'Manrope, sans-serif',
  color: 'var(--ink)',
  outline: 'none',
};
